import { useEffect, useMemo, useState } from 'react';
import {
  FaBuilding,
  FaBoxOpen,
  FaCalendarDay,
  FaCalendarDays,
  FaCalendarWeek,
  FaCircleInfo,
  FaDatabase,
  FaKey,
  FaLock,
  FaMoneyBillWave,
  FaRecycle,
  FaTags,
  FaTriangleExclamation,
  FaTrash,
  FaTruck,
  FaUser,
  FaUserPlus,
} from 'react-icons/fa6';

import Modal from '../../components/ui/Modal';
import SearchBar from '../../components/ui/SearchBar';
import StatusMessage from '../../components/ui/StatusMessage';
import { useAuth } from '../../context/AuthContext';
import {
  filterActivityLogs,
  formatActivityDateTime,
  getActivityLogs,
  searchActivityLogs,
} from '../../services/activityService';

const PAGE_SIZE = 25;

const eventTypeOptions = [
  { value: 'all', label: 'All Actions' },
  { value: 'user', label: 'User Actions' },
  { value: 'supplier', label: 'Supplier Actions' },
  { value: 'product', label: 'Product Actions' },
  { value: 'category', label: 'Category Actions' },
  { value: 'system', label: 'System Actions' },
];

const dateRangeOptions = [
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: 'all', label: 'All Time' },
];

const severityOptions = [
  { value: 'all', label: 'All' },
  { value: 'info', label: 'Info' },
  { value: 'success', label: 'Success' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
];

const eventCopy = {
  'user.invited': { action: 'invited', preposition: '' },
  'user.created': { action: 'created user', preposition: '' },
  'user.activated': { action: 'activated', preposition: '' },
  'user.deactivated': { action: 'deactivated', preposition: '' },
  'user.reactivated': { action: 'reactivated', preposition: '' },
  'user.deleted': { action: 'deleted', preposition: '' },
  'user.role_updated': { action: 'updated role', preposition: 'for' },
  'user.password_reset_requested': {
    action: 'requested password reset',
    preposition: 'for',
  },
  'user.invitation_accepted': { action: 'accepted invitation', preposition: '' },
  'supplier.created': { action: 'created supplier', preposition: '' },
  'supplier.updated': { action: 'updated supplier', preposition: '' },
  'supplier.soft_deleted': { action: 'moved supplier to trash', preposition: '' },
  'supplier.restored': { action: 'restored supplier', preposition: '' },
  'supplier.permanently_deleted': { action: 'permanently deleted supplier', preposition: '' },
  'supplier.contact_created': { action: 'created supplier contact', preposition: '' },
  'supplier.contact_updated': { action: 'updated supplier contact', preposition: '' },
  'supplier.contact_deleted': { action: 'deleted supplier contact', preposition: '' },
  'supplier.contact_primary_changed': { action: 'changed primary supplier contact', preposition: 'to' },
  'product.created': { action: 'created product', preposition: '' },
  'product.updated': { action: 'updated product', preposition: '' },
  'product.soft_deleted': { action: 'moved product to trash', preposition: '' },
  'product.restored': { action: 'restored product', preposition: '' },
  'product.permanently_deleted': { action: 'permanently deleted product', preposition: '' },
  'product.variant_created': { action: 'created product variant', preposition: '' },
  'product.variant_updated': { action: 'updated product variant', preposition: '' },
  'product.variant_removed': { action: 'removed product variant', preposition: '' },
  'product.price_updated': { action: 'updated price', preposition: 'for' },
  'product.price_history_added': { action: 'added price history', preposition: 'for' },
  'product.supplier_price_added': { action: 'added supplier price', preposition: 'for' },
  'product.supplier_linked': { action: 'linked supplier', preposition: 'to' },
  'product.supplier_unlinked': { action: 'unlinked supplier', preposition: 'from' },
  'configuration.created': { action: 'created configuration', preposition: '' },
  'configuration.updated': { action: 'updated configuration', preposition: '' },
  'configuration.saved': { action: 'saved configuration', preposition: '' },
  'configuration.duplicated': { action: 'duplicated configuration', preposition: '' },
  'configuration.soft_deleted': { action: 'moved configuration to trash', preposition: '' },
  'configuration.restored': { action: 'restored configuration', preposition: '' },
  'configuration.permanently_deleted': { action: 'permanently deleted configuration', preposition: '' },
  'configuration.recommended_enabled': { action: 'enabled recommended configuration', preposition: '' },
  'configuration.recommended_disabled': { action: 'disabled recommended configuration', preposition: '' },
  'configuration.component_added': { action: 'added component', preposition: 'to' },
  'configuration.component_removed': { action: 'removed component', preposition: 'from' },
  'configuration.supplier_changed': { action: 'changed supplier', preposition: 'for' },
  'category.created': { action: 'created category', preposition: '' },
  'category.updated': { action: 'updated category', preposition: '' },
  'category.soft_deleted': { action: 'moved category to trash', preposition: '' },
  'category.restored': { action: 'restored category', preposition: '' },
  'category.permanently_deleted': { action: 'permanently deleted category', preposition: '' },
  'system.restore_started': { action: 'started database restore', preposition: '' },
  'system.restore_completed': { action: 'completed database restore', preposition: '' },
  'system.restore_failed': { action: 'failed database restore', preposition: '' },
  'system.export_generated': { action: 'generated export', preposition: '' },
  'system.backup_created': { action: 'created backup', preposition: '' },
};

const eventIconMap = {
  'user.invited': FaUser,
  'user.role_updated': FaKey,
  'user.password_reset_requested': FaLock,
  'supplier.created': FaTruck,
  'supplier.updated': FaBuilding,
  'supplier.soft_deleted': FaTrash,
  'supplier.restored': FaRecycle,
  'supplier.permanently_deleted': FaTrash,
  'product.updated': FaBoxOpen,
  'product.variant_removed': FaTrash,
  'product.price_updated': FaMoneyBillWave,
  'product.supplier_price_added': FaMoneyBillWave,
  'system.restore_started': FaDatabase,
  'system.restore_completed': FaDatabase,
  'system.restore_failed': FaTriangleExclamation,
};

const getActorName = (activityLog) =>
  activityLog?.actor?.name ||
  activityLog?.actor?.displayName ||
  activityLog?.actor?.email ||
  'System';

const looksLikeFirestoreId = (value) => {
  const normalizedValue = String(value || '').trim();

  return /^[A-Za-z0-9_-]{16,}$/.test(normalizedValue);
};

const hasIdOnlyLabel = (value) =>
  String(value || '')
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean)
    .some(looksLikeFirestoreId);

const buildRelationLabel = (metadata = {}) => {
  const supplierName = String(metadata.supplierName || '').trim();
  const productName = String(metadata.productName || '').trim();
  const labelParts = [supplierName, productName].filter(Boolean);

  return labelParts.length ? labelParts.join(' / ') : '';
};

const getEntityLabel = (activityLog) => {
  const metadata = activityLog?.metadata || {};
  const eventType = activityLog?.eventType;

  if (
    [
      'product.supplier_linked',
      'product.supplier_unlinked',
      'product.supplier_price_added',
      'product.price_updated',
    ].includes(eventType)
  ) {
    const relationLabel = buildRelationLabel(metadata);

    if (relationLabel) {
      return relationLabel;
    }
  }

  if (eventType === 'configuration.supplier_changed') {
    return (
      activityLog?.entityLabel ||
      metadata.newSupplierName ||
      metadata.oldSupplierName ||
      'configuration supplier'
    );
  }

  if (eventType === 'product.price_history_added') {
    const priceLabel = [metadata.productName, metadata.variantName]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(' ');

    if (priceLabel) {
      return `${priceLabel} price`;
    }
  }

  if (activityLog?.entityLabel && !hasIdOnlyLabel(activityLog.entityLabel)) {
    return activityLog.entityLabel;
  }

  if (eventType?.includes('price')) {
    return 'product price';
  }

  if (eventType?.startsWith('product.supplier_')) {
    return 'supplier relation';
  }

  return activityLog?.entityType || 'record';
};

const getSeverityLabel = (severity) => {
  const normalizedSeverity = String(severity || 'info').toLowerCase();

  return normalizedSeverity.charAt(0).toUpperCase() + normalizedSeverity.slice(1);
};

const getSeverityClassName = (severity) => {
  const normalizedSeverity = String(severity || 'info').toLowerCase();

  if (['success', 'warning', 'critical'].includes(normalizedSeverity)) {
    return `activity-severity activity-severity--${normalizedSeverity}`;
  }

  return 'activity-severity activity-severity--info';
};

const normalizeDate = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const startOfWeek = (date) => {
  const start = startOfDay(date);
  const day = start.getDay();
  const diff = day === 0 ? 6 : day - 1;

  start.setDate(start.getDate() - diff);
  return start;
};

const startOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const formatRelativeTime = (value) => {
  const date = normalizeDate(value);

  if (!date) {
    return 'Unknown time';
  }

  const diffSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));

  if (diffSeconds < 60) {
    return 'Just now';
  }

  const diffMinutes = Math.floor(diffSeconds / 60);

  if (diffMinutes < 60) {
    return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);

  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  }

  const diffDays = Math.floor(diffHours / 24);

  if (diffDays < 7) {
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  }

  return formatActivityDateTime(date);
};

const getActivityDomain = (activityLog) => {
  const eventPrefix = String(activityLog?.eventType || '').split('.')[0];
  const domain = String(activityLog?.domain || '').toLowerCase();
  const entityType = String(activityLog?.entityType || '').toLowerCase();

  if (eventPrefix === 'user' || domain === 'users' || entityType === 'user') {
    return 'user';
  }

  if (eventPrefix === 'supplier' || domain === 'suppliers' || entityType.startsWith('supplier')) {
    return 'supplier';
  }

  if (
    eventPrefix === 'product' ||
    eventPrefix === 'configuration' ||
    domain === 'products' ||
    entityType === 'configuration' ||
    entityType.startsWith('product')
  ) {
    return 'product';
  }

  if (eventPrefix === 'category' || domain === 'categories' || entityType === 'category') {
    return 'category';
  }

  return 'system';
};

const getEventIcon = (activityLog) => {
  const eventType = activityLog?.eventType || '';
  const domain = getActivityDomain(activityLog);

  if (eventIconMap[eventType]) {
    return eventIconMap[eventType];
  }

  if (eventType.includes('deleted')) {
    return FaTrash;
  }

  if (eventType.includes('restored')) {
    return FaRecycle;
  }

  if (domain === 'supplier') {
    return FaTruck;
  }

  if (domain === 'product') {
    return FaBoxOpen;
  }

  if (domain === 'category') {
    return FaTags;
  }

  if (domain === 'user') {
    return FaUserPlus;
  }

  return FaCircleInfo;
};

const getEventActionCopy = (activityLog) => {
  const configuredCopy = eventCopy[activityLog?.eventType];

  if (configuredCopy) {
    return configuredCopy;
  }

  const action = String(activityLog?.action || activityLog?.eventType || 'performed action')
    .replace(/_/g, ' ')
    .replace(/\./g, ' ');

  return { action, preposition: activityLog?.entityLabel ? 'for' : '' };
};

const eventTypeLabelOverrides = {
  'user.invitation_accepted': 'Invitation Accepted',
  'user.password_reset_requested': 'Password Reset Requested',
  'system.restore_started': 'Restore Started',
  'system.restore_completed': 'Restore Completed',
  'system.restore_failed': 'Restore Failed',
  'system.export_generated': 'Export Generated',
  'system.backup_created': 'Backup Created',
};

const toTitleCase = (value) =>
  String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const getEventTypeLabel = (eventType) => {
  const normalizedEventType = String(eventType || '').trim();

  if (!normalizedEventType) {
    return 'Action';
  }

  if (eventTypeLabelOverrides[normalizedEventType]) {
    return eventTypeLabelOverrides[normalizedEventType];
  }

  const [domain, ...actionParts] = normalizedEventType.split('.');
  const readableAction = actionParts.join(' ').replace(/_/g, ' ');

  if (!readableAction) {
    return toTitleCase(domain);
  }

  return toTitleCase(`${domain} ${readableAction}`);
};

const hasObjectValues = (value) =>
  value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).length > 0;

const hasArrayValues = (value) => Array.isArray(value) && value.length > 0;

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value?.toDate === 'function') {
    return formatActivityDateTime(value);
  }

  if (value instanceof Date) {
    return formatActivityDateTime(value);
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
};

const getSummaryCounts = (activityLogs) => {
  const now = new Date();
  const todayStart = startOfDay(now);
  const weekStart = startOfWeek(now);
  const monthStart = startOfMonth(now);

  return activityLogs.reduce(
    (counts, activityLog) => {
      const occurredAt = normalizeDate(activityLog?.occurredAtDate);

      if (!occurredAt) {
        return counts;
      }

      if (occurredAt >= todayStart) {
        counts.today += 1;
      }

      if (occurredAt >= weekStart) {
        counts.week += 1;
      }

      if (occurredAt >= monthStart) {
        counts.month += 1;
      }

      return counts;
    },
    { today: 0, week: 0, month: 0 }
  );
};

const DetailItem = ({ label, value }) => (
  <div className="activity-detail-item">
    <dt>{label}</dt>
    <dd>{value || '-'}</dd>
  </div>
);

const JsonBlock = ({ value }) => (
  <pre className="activity-json-block">{formatValue(value)}</pre>
);

const KeyValueList = ({ value }) => {
  if (!hasObjectValues(value)) {
    return null;
  }

  return (
    <dl className="activity-key-value-list">
      {Object.entries(value).map(([key, itemValue]) => (
        <div key={key} className="activity-key-value-item">
          <dt>{key}</dt>
          <dd>
            {itemValue && typeof itemValue === 'object' ? (
              <JsonBlock value={itemValue} />
            ) : (
              formatValue(itemValue)
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

const renderChangesTable = (changes) => {
  if (!hasObjectValues(changes)) {
    return null;
  }

  return (
    <div className="activity-changes-list">
      {Object.entries(changes).map(([field, change]) => (
        <article key={field} className="activity-change-card">
          <h5>{field}</h5>
          <div className="activity-change-values">
            <div>
              <span>Before</span>
              <JsonBlock value={change?.old ?? change?.before} />
            </div>
            <div>
              <span>After</span>
              <JsonBlock value={change?.new ?? change?.after} />
            </div>
          </div>
        </article>
      ))}
    </div>
  );
};

const ActivityFeedItem = ({ activityLog, onOpen }) => {
  const Icon = getEventIcon(activityLog);
  const actorName = getActorName(activityLog);
  const targetLabel = getEntityLabel(activityLog);
  const { action, preposition } = getEventActionCopy(activityLog);
  const actionTypeLabel = getEventTypeLabel(activityLog.eventType);

  return (
    <button
      type="button"
      className="activity-feed-item"
      onClick={() => onOpen(activityLog)}
    >
      <span className={`activity-feed-icon activity-feed-icon--${getActivityDomain(activityLog)}`}>
        <Icon aria-hidden="true" />
      </span>

      <span className="activity-feed-content">
        <span className="activity-feed-main">
          <strong>{actorName}</strong>
          <span>{action}</span>
          {preposition ? <span>{preposition}</span> : null}
          <strong>{targetLabel}</strong>
        </span>

        <span className="activity-feed-meta">
          <span>{formatRelativeTime(activityLog.occurredAtDate)}</span>
          <span className="activity-feed-type">{actionTypeLabel}</span>
        </span>

        {activityLog.summary ? (
          <span className="activity-feed-summary">{activityLog.summary}</span>
        ) : null}
      </span>

      <span className={getSeverityClassName(activityLog.severity)}>
        {getSeverityLabel(activityLog.severity)}
      </span>
    </button>
  );
};

export default function ActivityPage() {
  const { role } = useAuth();
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [eventType, setEventType] = useState('all');
  const [dateRange, setDateRange] = useState('7d');
  const [severity, setSeverity] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    const loadActivityLogs = async () => {
      setLoading(true);
      setPageError('');

      try {
        const data = await getActivityLogs(role);

        setActivityLogs(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching actions:', error);
        setActivityLogs([]);
        setPageError('Failed to load actions.');
      } finally {
        setLoading(false);
      }
    };

    loadActivityLogs();
  }, [role]);

  const summaryCounts = useMemo(() => getSummaryCounts(activityLogs), [activityLogs]);

  const filteredLogs = useMemo(() => {
    const filteredByControls = filterActivityLogs(activityLogs, {
      eventType,
      dateRange,
      severity,
    });

    return searchActivityLogs(filteredByControls, searchTerm);
  }, [activityLogs, dateRange, eventType, searchTerm, severity]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const firstVisibleRow =
    filteredLogs.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const lastVisibleRow = Math.min(currentPage * PAGE_SIZE, filteredLogs.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, eventType, searchTerm, severity]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePreviousPage = () => {
    setCurrentPage((page) => Math.max(1, page - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((page) => Math.min(totalPages, page + 1));
  };

  return (
    <div className="page page--compact-list activity-page">
      <div className="page-header activity-page-header">
        <div className="page-title-block">
          <h1 className="page-title">Action Center</h1>
          <p className="page-subtitle">
            Track all important actions across Vacanza OS.
          </p>
        </div>
      </div>

      <StatusMessage tone="error" message={pageError} />

      <section className="activity-summary-grid" aria-label="Action summary">
        <article className="activity-summary-card">
          <span className="activity-summary-card__icon" aria-hidden="true">
            <FaCalendarDay />
          </span>
          <span className="activity-summary-card__content">
            <span>Today's Actions</span>
            <strong>{summaryCounts.today}</strong>
          </span>
        </article>
        <article className="activity-summary-card">
          <span className="activity-summary-card__icon" aria-hidden="true">
            <FaCalendarWeek />
          </span>
          <span className="activity-summary-card__content">
            <span>This Week</span>
            <strong>{summaryCounts.week}</strong>
          </span>
        </article>
        <article className="activity-summary-card">
          <span className="activity-summary-card__icon" aria-hidden="true">
            <FaCalendarDays />
          </span>
          <span className="activity-summary-card__content">
            <span>This Month</span>
            <strong>{summaryCounts.month}</strong>
          </span>
        </article>
      </section>

      <section className="section activity-filter-card">
        <div className="activity-toolbar">
          <div className="activity-toolbar__search">
            <SearchBar
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search actions..."
            />
          </div>

          <select
            className="select activity-toolbar__select"
            value={eventType}
            onChange={(event) => setEventType(event.target.value)}
            aria-label="Action type"
          >
            {eventTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="select activity-toolbar__select"
            value={dateRange}
            onChange={(event) => setDateRange(event.target.value)}
            aria-label="Date range"
          >
            {dateRangeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            className="select activity-toolbar__select"
            value={severity}
            onChange={(event) => setSeverity(event.target.value)}
            aria-label="Severity"
          >
            {severityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="section activity-feed-section">
        <div className="activity-feed-header">
          <div>
            <h2 className="section-title">Recent Actions</h2>
            <p className="page-subtitle">
              Showing {firstVisibleRow}-{lastVisibleRow} of {filteredLogs.length}
            </p>
          </div>
        </div>

        {loading ? <p className="page-subtitle">Loading actions...</p> : null}

        {!loading && paginatedLogs.length === 0 ? (
          <div className="activity-empty-state">
            No actions match the current filters.
          </div>
        ) : null}

        {!loading && paginatedLogs.length > 0 ? (
          <div className="activity-feed-list">
            {paginatedLogs.map((activityLog) => (
              <ActivityFeedItem
                key={activityLog.id}
                activityLog={activityLog}
                onOpen={setSelectedLog}
              />
            ))}
          </div>
        ) : null}

        {!loading ? (
          <div className="activity-pagination">
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <div className="activity-pagination__actions">
              <button
                type="button"
                className="button"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                type="button"
                className="button"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <Modal
        isOpen={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        title="Action Details"
        hideFooter
        maxWidth={780}
      >
        {selectedLog ? (
          <div className="activity-detail">
            <section className="activity-detail-card">
              <h4>Action Details</h4>
              <dl className="activity-detail-grid">
                <DetailItem label="Action Type" value={getEventTypeLabel(selectedLog.eventType)} />
                <DetailItem label="Domain" value={selectedLog.domain} />
                <DetailItem label="Severity" value={getSeverityLabel(selectedLog.severity)} />
                <DetailItem
                  label="Occurred At"
                  value={formatActivityDateTime(selectedLog.occurredAtDate)}
                />
                <DetailItem label="Summary" value={selectedLog.summary} />
              </dl>
            </section>

            <section className="activity-detail-card">
              <h4>Actor</h4>
              <dl className="activity-detail-grid">
                <DetailItem label="Name" value={getActorName(selectedLog)} />
                <DetailItem label="Email" value={selectedLog.actor?.email} />
                <DetailItem label="Role" value={selectedLog.actor?.role} />
                <DetailItem label="UID" value={selectedLog.actor?.uid} />
              </dl>
            </section>

            <section className="activity-detail-card">
              <h4>Target</h4>
              <dl className="activity-detail-grid">
                <DetailItem label="Entity" value={getEntityLabel(selectedLog)} />
                <DetailItem label="Entity Type" value={selectedLog.entityType} />
                <DetailItem label="Entity ID" value={selectedLog.entityId} />
                <DetailItem label="Action" value={selectedLog.action} />
              </dl>
            </section>

            {(hasArrayValues(selectedLog.changedFields) ||
              hasObjectValues(selectedLog.changes) ||
              hasObjectValues(selectedLog.before) ||
              hasObjectValues(selectedLog.after)) ? (
                <section className="activity-detail-card">
                  <h4>Changes</h4>
                  {hasArrayValues(selectedLog.changedFields) ? (
                    <div className="tag-list activity-changed-fields">
                      {selectedLog.changedFields.map((field) => (
                        <span key={field} className="tag">
                          {field}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {renderChangesTable(selectedLog.changes)}
                  {!hasObjectValues(selectedLog.changes) && hasObjectValues(selectedLog.before) ? (
                    <JsonBlock value={selectedLog.before} />
                  ) : null}
                  {!hasObjectValues(selectedLog.changes) && hasObjectValues(selectedLog.after) ? (
                    <JsonBlock value={selectedLog.after} />
                  ) : null}
                </section>
              ) : null}

            {hasObjectValues(selectedLog.metadata) ? (
              <section className="activity-detail-card">
                <h4>Metadata</h4>
                <KeyValueList value={selectedLog.metadata} />
              </section>
            ) : null}

            <div className="modal-actions">
              <button
                type="button"
                className="button button-primary"
                onClick={() => setSelectedLog(null)}
              >
                Close
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
